export default class GalleryManager {
  constructor(selectors, component, moduleLoader) {
    this.moduleLoader = moduleLoader;
    this.component = component;
    this.SELECTORS = selectors;
    
    this.itemsContainer = this.component.querySelector(
      this.SELECTORS.itemsContainer
    );
    this.items = this.component.querySelectorAll(this.SELECTORS.item);
    this.arrowRight = this.component.querySelector(this.SELECTORS.arrowRight);
    this.arrowLeft = this.component.querySelector(this.SELECTORS.arrowLeft);
    this.scroller = this.component.querySelector(this.SELECTORS.scroller);

    this.item = this.items[0];
    this.scrollIndex = 0;
    this.itemWidths = [];
    this.cumulativePositions = [];
    this.resizeTimeout = null;
    this.scrollTimeout = null;
    this.isArrowClick = false;

    if (this.scroller) {
      this.init();
    }
  }

  init() {
    this.calculateItemWidths();
    this.calculateDimensions();
    
    this.scrollIndex = 0;
    this.scroller.scrollLeft = 0;
    this.updateArrowVisibility();
    
    if (this.maxIndex > 0) {
      this.component.querySelector('.shortcut__wrapper').style.justifyContent = 'start';
    }

    this.bindEvents();
    this.bindResizeEvent();
    this.bindScrollEvents();
  }

  calculateItemWidths() {
    this.itemWidths = Array.from(this.items).map(item => item.offsetWidth + 8);
    this.cumulativePositions = [0];
    
    for (let i = 0; i < this.itemWidths.length - 1; i++) {
      this.cumulativePositions.push(
        this.cumulativePositions[i] + this.itemWidths[i]
      );
    }
  }

  bindEvents() {
    this.arrowRight.addEventListener('click', () => {
      this.isArrowClick = true;
      
      const currentScrollLeft = this.scroller.scrollLeft;
      const maxScroll = this.scroller.scrollWidth - this.scroller.offsetWidth;
      
      if (currentScrollLeft >= maxScroll - 10) {
        this.scrollIndex = this.maxIndex;
        this.scroller.scrollTo({
          left: this.maxScrollPosition,
          behavior: 'smooth'
        });
      } else {
        let nextPosition = this.maxScrollPosition;
        
        for (let i = 0; i < this.cumulativePositions.length; i++) {
          if (this.cumulativePositions[i] > currentScrollLeft + 5) {
            nextPosition = this.cumulativePositions[i];
            this.scrollIndex = i;
            break;
          }
        }
        
        if (nextPosition === this.maxScrollPosition) {
          this.scrollIndex = this.maxIndex;
        }
        
        this.scroller.scrollTo({
          left: nextPosition,
          behavior: 'smooth'
        });
      }
      
      setTimeout(() => {
        this.isArrowClick = false;
      }, 300);
    });
    
    this.arrowLeft.addEventListener('click', () => {
      this.isArrowClick = true;
      
      const currentScrollLeft = this.scroller.scrollLeft;
      
      if (currentScrollLeft <= 10) {
        this.scrollIndex = 0;
        this.scroller.scrollTo({
          left: 0,
          behavior: 'smooth'
        });
      } else {
        let prevPosition = 0;
        this.scrollIndex = 0;
        
        for (let i = this.cumulativePositions.length - 1; i >= 0; i--) {
          if (this.cumulativePositions[i] < currentScrollLeft - 5) {
            prevPosition = this.cumulativePositions[i];
            this.scrollIndex = i;
            break;
          }
        }
        
        this.scroller.scrollTo({
          left: prevPosition,
          behavior: 'smooth'
        });
      }
      
      setTimeout(() => {
        this.isArrowClick = false;
      }, 300);
    });
  }

  bindResizeEvent() {
    window.addEventListener('resize', () => {
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }
      
      this.resizeTimeout = setTimeout(() => {
        this.handleResize();
      }, 150);
    });
  }

  bindScrollEvents() {
    this.scroller.addEventListener('scroll', () => {
      this.updateArrowVisibility();
      
      if (!this.isArrowClick) {
        if (this.scrollTimeout) {
          clearTimeout(this.scrollTimeout);
        }
        
        this.scrollTimeout = setTimeout(() => {
          this.syncScrollPosition();
        }, 50);
      }
    });

    let startX = 0;
    let scrollLeft = 0;

    this.scroller.addEventListener('touchstart', (e) => {
      startX = e.touches[0].pageX - this.scroller.offsetLeft;
      scrollLeft = this.scroller.scrollLeft;
    });

    this.scroller.addEventListener('touchmove', (e) => {
      if (!startX) return;
      const x = e.touches[0].pageX - this.scroller.offsetLeft;
      const walk = (x - startX) * 1.2;
      this.scroller.scrollLeft = scrollLeft - walk;
    });

    this.scroller.addEventListener('touchend', () => {
      startX = 0;
    });
  }

  handleScroll() {
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
    
    this.scrollTimeout = setTimeout(() => {
      this.syncScrollPosition();
    }, 10);
    
    this.updateArrowVisibility();
  }

  syncScrollPosition() {
    const currentScrollLeft = this.scroller.scrollLeft;
    
    let closestIndex = 0;
    let minDistance = Math.abs(currentScrollLeft);
    
    for (let i = 0; i < this.cumulativePositions.length; i++) {
      const distance = Math.abs(currentScrollLeft - this.cumulativePositions[i]);
      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = i;
      }
    }
    
    const maxScroll = this.scroller.scrollWidth - this.scroller.offsetWidth;
    if (currentScrollLeft >= maxScroll - 10) {
      closestIndex = this.maxIndex;
    }
    
    this.scrollIndex = Math.min(Math.max(closestIndex, 0), this.maxIndex);
  }

  handleResize() {
    this.calculateItemWidths();
    this.calculateDimensions();
    
    if (this.scrollIndex > this.maxIndex) {
      this.scrollIndex = this.maxIndex;
    }
    
    this.updateScrollPosition();
    
    if (this.maxIndex > 0) {
      this.component.querySelector('.shortcut__wrapper').style.justifyContent = 'start';
    } else {
      this.component.querySelector('.shortcut__wrapper').style.justifyContent = '';
    }
  }

  calculateDimensions() {
    const visibleWidth = this.scroller.offsetWidth;
    const totalWidth = this.itemWidths.reduce((sum, width) => sum + width, 0);

    if (totalWidth <= visibleWidth) {
      this.maxIndex = 0;
      this.maxScrollPosition = 0;
      return;
    }

    const extraPadding = 16; 
    this.maxScrollPosition = totalWidth - visibleWidth + extraPadding;
    
    this.maxIndex = this.cumulativePositions.length - 1;
    for (let i = this.cumulativePositions.length - 1; i >= 0; i--) {
      if (this.cumulativePositions[i] <= this.maxScrollPosition) {
        this.maxIndex = i;
        break;
      }
    }
    
    const actualMaxScroll = this.scroller.scrollWidth - this.scroller.offsetWidth;
    this.maxScrollPosition = Math.min(this.maxScrollPosition, actualMaxScroll);
  }

  updateScrollPosition() {
    let scrollPosition;
    
    if (this.scrollIndex >= this.maxIndex) {
      scrollPosition = this.maxScrollPosition;
    } else {
      scrollPosition = this.cumulativePositions[this.scrollIndex];
    }
    
    this.scroller.scrollTo({
      left: scrollPosition,
      behavior: this.isArrowClick ? 'smooth' : 'auto'
    });
    
    if (this.isArrowClick) {
      setTimeout(() => {
        this.isArrowClick = false;
      }, 300);
    }
  }

  updateArrowVisibility() {
    const currentScrollLeft = this.scroller.scrollLeft;
    const maxScroll = this.scroller.scrollWidth - this.scroller.offsetWidth;
    
    if (currentScrollLeft <= 5) {
      this.arrowLeft.style.display = 'none';
    } else {
      this.arrowLeft.style.display = 'flex';
    }

    if (currentScrollLeft >= maxScroll - 5) {
      this.arrowRight.style.display = 'none';
    } else {
      this.arrowRight.style.display = 'flex';
    }
    
    if (maxScroll <= 0) {
      this.arrowLeft.style.display = 'none';
      this.arrowRight.style.display = 'none';
    }
  }

  destroy() {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
    window.removeEventListener('resize', this.handleResize);
  }
}
